import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database } from 'lucide-react';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-4xl mx-auto p-6 md:p-12 space-y-8 text-gray-800">
      <button 
        id="btn-privacy-back"
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-500 hover:text-gray-900 mb-6 font-bold text-xs uppercase tracking-wider cursor-pointer"
      >
        <ArrowLeft size={16} className="mr-2" />
        Go Back
      </button>

      <div className="border-b border-gray-200 pb-6 space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 border border-emerald-100 rounded-full text-[10px] font-black uppercase tracking-widest text-emerald-700">
          <Shield size={12} />
          Data Protection & Privacy
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight uppercase italic">Privacy Policy</h1>
        <p className="text-xs text-gray-500 font-medium">Last Updated: August 26, 2026</p>
      </div>
      
      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">1. Introduction & Entity Scope</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          This Privacy Policy outlines the policies and practices of <strong>GrowEzi AI</strong>, a d/b/a of <strong>Data Driven Bookkeeping LLC</strong> ("Company," "we," "our," or "us"), regarding the collection, storage, processing, and protection of information obtained through <strong>TipValue AI</strong> ("Platform," "Service," or "Application").
        </p>
        <p className="text-sm leading-relaxed text-gray-600">
          We are committed to maintaining the confidentiality and integrity of restaurant operational data, payroll information, and employee gratuity records.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">2. Information We Collect</h2>
        <p className="text-sm text-gray-600">We process data solely to deliver automated tip reconciliation, shift allocation calculations, and payroll formatting:</p>
        <ul className="list-disc pl-6 space-y-2 text-sm text-gray-600">
          <li><strong>Point of Sale (POS) Data:</strong> Transaction totals, sales categories, credit card tip amounts, cash receipts, and timecard clock-in/out timestamps synced via connected POS integrations or manual file uploads.</li>
          <li><strong>Tenant & Location Information:</strong> Restaurant or hospitality entity names, DBAs, Federal EINs, operating physical state/jurisdiction, shift schedule configurations, and point-weighting formulas.</li>
          <li><strong>Employee & Roster Data:</strong> Staff member names, job classifications (e.g., Server, Bartender, Busser), system user IDs, and digital sign-off records.</li>
          <li><strong>Account & Billing Information:</strong> Contact emails, administrative authentication identifiers (via secure Google Authentication), and subscription billing status.</li>
        </ul>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">3. How We Use & Process Data</h2>
        <p className="text-sm text-gray-600 leading-relaxed">
          Collected data is processed strictly for legitimate business and contractual purposes, including:
        </p>
        <ul className="list-disc pl-6 space-y-1.5 text-sm text-gray-600">
          <li>Executing accurate shift-based tip pool mathematical calculations and role point allocations.</li>
          <li>Generating employee digital sign-off records and transparent shift verification breakdowns.</li>
          <li>Formatting pre-reconciled export files for payroll systems (Gusto, ADP, Paychex).</li>
          <li>Running internal IRS Form 8027 8% shortfall tracking and state credit card offset safeguards.</li>
        </ul>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">4. No Sale or Monetization of Customer Data</h2>
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-900 text-sm leading-relaxed">
          <p className="font-bold mb-1">Our Core Privacy Commitment:</p>
          <p>
            We do NOT sell, rent, monetize, or trade your restaurant data, employee payroll records, or POS financial transactions to third parties, data brokers, or advertising networks under any circumstances.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">5. Multi-Tenant Data Isolation & Security</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          TipValue AI implements rigorous multi-tenant data partitioning. Each restaurant entity's data is isolated and secured using granular database access control rules. All network communications are encrypted in transit via Transport Layer Security (TLS/HTTPS) and stored in encrypted cloud databases.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">6. Data Retention & Deletion</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          We retain historical tip allocation, dispute, and payroll records for the duration of your active subscription to maintain auditable operational histories. Customers may request the export or deletion of their tenant account data upon service termination.
        </p>
      </div>

      <div className="space-y-4 border-t border-gray-200 pt-6">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">7. Contact & Privacy Inquiries</h2>
        <p className="text-sm text-gray-600">
          For privacy inquiries, data deletion requests, or compliance questions, please contact:
        </p>
        <div className="p-4 bg-gray-50 rounded-xl text-xs text-gray-700 space-y-1">
          <p className="font-bold">GrowEzi AI (d/b/a Data Driven Bookkeeping LLC)</p>
          <p>Contact Email: <a href="mailto:admin@growezy.ai" className="text-blue-600 underline">admin@growezy.ai</a></p>
        </div>
      </div>
    </div>
  );
};
